/**
 * The signed-in frame: the shared Shell with this app's navigation and live data slotted in.
 * The current route's title, crumbs and nav area come from its `handle` (router.tsx).
 *
 * Top bar, in the README's order: title and crumbs · environment switcher · capacity meter ·
 * live indicator · jobs · cloud shell · support · notifications · Ask Inferrex (⌘J).
 * Jobs, the cloud shell and Ask Inferrex open working panels that say they arrive in a later step;
 * nothing in them is invented.
 */
import { useMemo, useState } from 'react';
import { Outlet, useMatches, useNavigate } from 'react-router-dom';
import { Bell, LifeBuoy, Loader, SquareTerminal } from 'lucide-react';
import {
  CommandPalette, EnvironmentDot, EnvironmentSwitcher, HeaderCapacityMeter, LaterStepPanelBody, LiveIndicator,
  NotificationList, Shell, SidePanel, TopBarButton, TopBarDivider, UserMenu, WorkspaceSwitcher,
  WORKSPACE_SIGN_IN_PATH, recordWorkspaceChoice, useCommandPalette, useMediaQuery, useThemePreference,
  SHELL_MOBILE_QUERY, StateBlock, type PaletteCommand, type ShellNotification,
} from '@inferrex/ui';
import { useAuth } from '@inferrex/ui/auth';
import {
  useActiveEnvironment, useEnvironmentsList, useMarkAllNotificationsRead, useMarkNotificationRead,
  useMyWorkspaces, useSwitchEnvironment, useSwitchWorkspace, useWorkspaceUsage,
} from '@inferrex/api-client';
import { FOOTER_NAV, MOBILE_TABS, NAV_GROUPS, mobileTabFor } from '../navigation';
import { SHELL_ROUTES, type AppRoute } from '../routes';
import { useLiveState } from '../lib/live-events';
import {
  activeShellEnvironment, capacityDimensions, toShellEnvironment, toShellWorkspace, useRecentNotifications, useUnreadCount,
} from './shell-data';

type Panel = 'notifications' | 'jobs' | 'shell' | 'ask' | null;

export const NOT_FOUND_ROUTE: AppRoute = { path: '*', title: 'Not found', nav: 'none', step: 0, what: '' };

function Logo() {
  return (
    <>
      <img className="ifx-logo--light" src="/logos/inferrex-lockup-light-600.png" alt="Inferrex" />
      <img className="ifx-logo--dark" src="/logos/inferrex-lockup-inverse-600.png" alt="Inferrex" />
    </>
  );
}

function LogoMark() {
  return <img src="/logos/inferrex-icon-128.png" alt="Inferrex" />;
}

/** ⌘K: pages for now. Search across systems, objects and records arrives with GET /v1/search (GAPS A4). */
function usePageCommands(navigate: (to: string) => void): PaletteCommand[] {
  return useMemo(() => {
    const out: PaletteCommand[] = [];
    for (const g of NAV_GROUPS) {
      for (const i of g.items) out.push({ id: i.key, label: i.label, group: g.label ?? 'Go to', run: () => navigate(i.to) });
    }
    for (const r of SHELL_ROUTES) {
      if (r.nav === 'settings' && !r.path.includes(':') && r.path !== '/settings') {
        out.push({ id: r.path, label: r.title, group: 'Workspace settings', run: () => navigate(r.path) });
      }
    }
    return out;
  }, [navigate]);
}

export function AppShellLayout() {
  const navigate = useNavigate();
  const matches = useMatches();
  const route = (matches[matches.length - 1]?.handle as { route?: AppRoute } | undefined)?.route ?? NOT_FOUND_ROUTE;
  const mobile = useMediaQuery(SHELL_MOBILE_QUERY);
  const [panel, setPanel] = useState<Panel>(null);
  const toggle = (p: Exclude<Panel, null>) => setPanel((cur) => (cur === p ? null : p));

  const { user, signOut } = useAuth();
  const theme = useThemePreference();
  const palette = useCommandPalette();
  const commands = usePageCommands(navigate);
  const live = useLiveState();

  const workspaces = useMyWorkspaces();
  const switchWorkspace = useSwitchWorkspace();
  const activeEnv = useActiveEnvironment();
  const environments = useEnvironmentsList();
  const switchEnvironment = useSwitchEnvironment();
  const usage = useWorkspaceUsage();
  const unread = useUnreadCount();
  const notifications = useRecentNotifications(panel === 'notifications');
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const wsList = (workspaces.data ?? []).map(toShellWorkspace);
  const activeWs = (workspaces.data ?? []).find((w) => w.active);
  const activeShellWs = activeWs ? toShellWorkspace(activeWs) : wsList[0] ?? null;
  const env = activeShellEnvironment(activeEnv.data);
  const envList = (environments.data ?? []).map(toShellEnvironment);
  const unreadCount = unread.data?.unreadCount ?? 0;
  const userName = user?.displayName || user?.email || 'Signed in';

  const openNotification = (n: ShellNotification) => {
    if (!n.read) markRead.mutate(n.id);
    setPanel(null);
    if (n.href) navigate(n.href);
  };

  const bell = (
    <TopBarButton icon={Bell} label="Notifications" badge={unreadCount} expanded={panel === 'notifications'} onClick={() => toggle('notifications')} />
  );

  const topBarItems = (
    <>
      {usage.data && <HeaderCapacityMeter dimensions={capacityDimensions(usage.data)} onClick={() => navigate('/settings/billing')} />}
      <LiveIndicator state={live} />
      <TopBarDivider />
      <TopBarButton icon={Loader} label="Jobs" expanded={panel === 'jobs'} onClick={() => toggle('jobs')} />
      <TopBarButton icon={SquareTerminal} label="Cloud shell" expanded={panel === 'shell'} onClick={() => toggle('shell')} />
      <TopBarButton icon={LifeBuoy} label="Help and support" popup={false} onClick={() => navigate('/settings/portal')} />
      {bell}
    </>
  );

  return (
    <>
      <Shell
        nav={NAV_GROUPS}
        footerNav={FOOTER_NAV}
        activeKey={route.nav}
        title={route.title}
        crumbs={route.crumbs}
        logo={<Logo />}
        logoMark={<LogoMark />}
        mobile={mobile}
        workspace={({ collapsed }) => (
          <WorkspaceSwitcher
            workspaces={wsList}
            active={activeShellWs}
            collapsed={collapsed}
            onSelect={(id) => { recordWorkspaceChoice(id); switchWorkspace(id); }}
            onUseWorkspaceId={() => window.location.assign(WORKSPACE_SIGN_IN_PATH)}
          />
        )}
        user={({ collapsed }) => (
          <UserMenu
            name={userName}
            detail={activeWs?.role}
            collapsed={collapsed}
            theme={theme.preference}
            onThemeChange={theme.setPreference}
            onAccount={() => navigate('/settings/account')}
            onSignOut={() => { void signOut(); }}
          />
        )}
        onSearch={() => palette.setOpen(true)}
        environment={<EnvironmentSwitcher environments={envList} active={env} onSelect={switchEnvironment} />}
        topBarItems={topBarItems}
        askOpen={panel === 'ask'}
        onAsk={() => toggle('ask')}
        mobileSubtitle={<><EnvironmentDot cls={env?.class} />{env?.name ?? 'Default environment'} · {activeShellWs?.name ?? 'Workspace'}</>}
        mobileActions={
          <button type="button" className="ifx-appbar__button" onClick={() => toggle('notifications')} aria-label={unreadCount ? `Notifications, ${unreadCount} unread` : 'Notifications'} aria-haspopup="dialog" aria-expanded={panel === 'notifications'}>
            <Bell aria-hidden="true" />
            {unreadCount > 0 && <span className="ifx-badge-dot" aria-hidden="true" />}
          </button>
        }
        tabs={MOBILE_TABS}
        activeTab={mobileTabFor(route.nav)}
        showTabs={!route.parent}
        back={route.parent ? { label: 'Back', onBack: () => navigate(route.parent ?? '/') } : undefined}
      >
        <Outlet />
      </Shell>

      <SidePanel
        open={panel === 'notifications'}
        onOpenChange={(o) => setPanel(o ? 'notifications' : null)}
        title="Notifications"
        mobile={mobile}
        actions={unreadCount > 0 ? (
          <button type="button" className="ifx-chip" onClick={() => markAllRead.mutate()} disabled={markAllRead.isPending}>Mark all read</button>
        ) : null}
      >
        {notifications.isLoading ? <StateBlock kind="loading" rows={4} message="Loading notifications…" />
          : notifications.error ? <StateBlock kind="error" message="Notifications didn’t load." retryable onRetry={() => { void notifications.refetch(); }} />
          : <NotificationList items={notifications.data ?? []} onOpen={openNotification} />}
      </SidePanel>
      <SidePanel open={panel === 'jobs'} onOpenChange={(o) => setPanel(o ? 'jobs' : null)} title="Jobs" mobile={mobile}>
        <LaterStepPanelBody what="Running and recent jobs (syncs, matches, reads of a system’s API)" step="a later step" />
      </SidePanel>
      <SidePanel open={panel === 'shell'} onOpenChange={(o) => setPanel(o ? 'shell' : null)} title="Cloud shell" mobile={mobile}>
        <LaterStepPanelBody what="The cloud shell, a terminal already signed in to Inferrex," step="a later step" />
      </SidePanel>
      <SidePanel open={panel === 'ask'} onOpenChange={(o) => setPanel(o ? 'ask' : null)} title="Ask Inferrex" mobile={mobile}>
        <LaterStepPanelBody what="Ask Inferrex" step="build step 7" />
      </SidePanel>
      <CommandPalette open={palette.open} onOpenChange={palette.setOpen} commands={commands} />
    </>
  );
}
